import PropTypes from 'prop-types';
import { withStyles, makeStyles } from '@mui/styles';
import MuiDialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery } from '@mui/material';
import OnboardingMenu from '../../dashboard/onboarding-menu';

const styles = theme => ({
  root: {
    margin: 0,
    padding: '20px 25px',
    minHeight: 30,
    '@media (max-width:768px)': {
      textAlign: 'center',
    },
  },
  title: {
    fontFamily: 'Roboto',
    marginRight: 32,
    fontSize: 28,
    '&.--title-center': {
      textAlign: 'center',
    },
    '@media (max-width:768px)': {
      margin: 'auto 24px auto auto',
      fontSize: 20,
    },
    '&.--without-closing': {
      margin: 'auto',
    },
  },
  closeButton: {
    position: 'absolute',
    right: 4,
    top: 10,
    color: 'white',
  },
  rootContent: {
    padding: theme.spacing(2),
    '&.-bg': {
      backgroundColor: '#f8f8f8',
    },
    '&.-unpaddingContent': {
      padding: 0,
    },
  },
  rootFooter: {
    margin: 0,
    padding: theme.spacing(1),
    '&.-unpaddingFooter': {
      padding: 0,
    },
  },
  dialog: {},
});

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, titleCenter, withoutClosing, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      {children}
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles(styles)(props => {
  const { classes, bg, unpaddingContent, ...other } = props;
  return (
    <MuiDialogContent
      className={`${classes.rootContent} ${bg ? '-bg' : ''} ${unpaddingContent ? '-unpaddingContent' : ''}`}
      {...other}
    />
  );
});

const DialogActions = withStyles(styles)(props => {
  const { classes, unpaddingFooter, ...other } = props;
  return <MuiDialogActions className={`${classes.rootFooter} ${unpaddingFooter ? '-unpaddingFooter' : ''}`} {...other} />;
});

const useDialogStyles = makeStyles(theme => ({
  root: {
    '& .MuiDialog-paper': {
      width: '100%',
      margin: 0,
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
    },
  },
}));

const OnboardingDialog = ({
  type = 'sm', // 'lg' | 'md' | 'xl' | 'xs' | 'sm',
  open = true,
  dividers = true,
  fullScreen = false,
  fullScreenMobile = false,
  unpaddingContent = false,
  unpaddingFooter = false,
  titleCenter = false,
  title,
  menuItems,
  body,
  footer,
  handleClose,
}) => {
  const isTabletMobile = useMediaQuery('(max-width:768px)');
  const classes = useDialogStyles();

  return (
    <div>
      <MuiDialog
        open={open}
        fullScreen={fullScreen || (fullScreenMobile && isTabletMobile)}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        className={classes.root}
      >
        <DialogContent dividers={dividers} bg={dividers} unpaddingContent={unpaddingContent}>
          <OnboardingMenu
            items={menuItems}
            header={
              (handleClose || title) && (
                <DialogTitle titleCenter={titleCenter} withoutClosing={!handleClose} onClose={handleClose}>
                  {title}
                </DialogTitle>
              )
            }
            body={body}
          />
        </DialogContent>
        {footer && <DialogActions unpaddingFooter={unpaddingFooter}>{footer}</DialogActions>}
      </MuiDialog>
    </div>
  );
};

OnboardingDialog.propTypes = {
  type: PropTypes.string,
  open: PropTypes.bool,
  dividers: PropTypes.bool,
  fullScreen: PropTypes.bool,
  fullScreenMobile: PropTypes.bool,
  unpaddingContent: PropTypes.bool,
  titleCenter: PropTypes.bool,
  title: PropTypes.string,
  body: PropTypes.object.isRequired,
  menuItems: PropTypes.array.isRequired,
  footer: PropTypes.object,
  handleClose: PropTypes.func,
};

export default OnboardingDialog;
